import React from 'react';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { DocumentNode, useQuery } from '@apollo/client';
import { ArrowLeftRegular, EditRegular } from '@fluentui/react-icons';
import { Button, makeStyles } from '@fluentui/react-components';
import { useHistory, useParams } from 'react-router-dom';
import Card from '../../common/card/Card';
import ShimmeredDetailsListWrapper from '../../common/DetailsLists/ShimmeredDetailsListWrapper';
import {
  bodyContentContainer,
  iconButtonStyles,
} from '../../../app/common/styles/CommonStyleObjects';
import FullWidthHeader from '../../common/headers/FullWidthHeader';
import CanAccess from '../../common/canAccess/CanAccess';
import SpanWithLabel from '../../common/SpanWithLabel';
import StackColumn from '../../common/StackColumn';

import getColumns from '../shared/EntityTerminationTaskList.config';
import {
  ILegalInProcessActionItemParams,
  IEntityTerminationTask,
  IEntityTerminationActionItem,
} from '../types';
import momentUtc from '../../../utils/DateFormatter';
import StatDateFormats from '../../../utils/types/DateFormats';
import LoadingErrorMessage from '../../common/errorContent/LoadingErrorMessage';
import EntityTerminationActionItemEditPanel from './EntityTerminationActionItemEditPanel';
import EntityTerminationTaskEditPanel from './EntityTerminationTaskEditPanel';

interface IEntityTerminationActionItemPageProps {
  /** Display name for this action item type (e.g. "Action Item", "Blocker"). Defaults to "Action Item". */
  actionItemName?: string;
  /** URL segment used for back-navigation (e.g. "legal-in-process", "finance-incomplete") */
  callerUrl: string;
  query: DocumentNode;
  /** Mutation used to save action item detail edits. */
  updateActionItemMutation: DocumentNode;
  /** GraphQL variable name for the action item input DTO. */
  dtoVariableName: string;
  /** Permission action required to edit the action item details (and tasks, when task editing is enabled). */
  editAction: string;
  /** When true, the description is read-only (template-based stages like Finance Incomplete). */
  isDescriptionReadOnly?: boolean;
  /** When true, includes companyCode in the detail query variables. Defaults to false. */
  includeCompanyCodeInQuery?: boolean;
  createTaskMutation?: DocumentNode;
  updateTaskMutation?: DocumentNode;
  /** GraphQL variable name for the task input DTO. Defaults to Legal In Process. */
  taskDtoVariableName?: string;
  /** Field name on the task DTO holding the parent action item id. Defaults to Legal In Process. */
  actionItemIdFieldName?: string;
  /** When true, tasks support document uploads in the add/edit task panel. */
  enableTaskDocuments?: boolean;
}

const useStyles = makeStyles({
  sectionSpacing: { marginBottom: '20px' },
});

const EntityTerminationActionItemPage = (
  props: IEntityTerminationActionItemPageProps,
): JSX.Element => {
  const {
    actionItemName,
    callerUrl,
    query,
    updateActionItemMutation: updateMutation,
    dtoVariableName,
    editAction,
    isDescriptionReadOnly = false,
    includeCompanyCodeInQuery = false,
    createTaskMutation,
    updateTaskMutation,
    taskDtoVariableName,
    actionItemIdFieldName,
    enableTaskDocuments = false,
  } = props;
  const history = useHistory();
  const { companyCode, actionItemId: actionItemIdParam } =
    useParams<ILegalInProcessActionItemParams>();
  const actionItemId = actionItemIdParam ? Number.parseInt(actionItemIdParam, 10) : NaN;

  const classes = useStyles();
  const actionItemTerm = actionItemName ?? 'Action Item';

  const queryVariables = includeCompanyCodeInQuery
    ? { companyCode, actionItemId }
    : { actionItemId };

  const { loading, error, data } = useQuery(query, {
    variables: queryVariables,
    fetchPolicy: 'cache-and-network',
  });

  const actionItem: IEntityTerminationActionItem | undefined = data?.actionItem;

  const refetchQuery = { query, variables: queryVariables };

  const [isEditActionDetailsPanelOpen, setIsEditActionDetailsPanelOpen] =
    React.useState<boolean>(false);
  const [isEditTaskPanelOpen, setIsEditTaskPanelOpen] = React.useState<boolean>(false);
  const [selectedTask, setSelectedTask] = React.useState<IEntityTerminationTask | undefined>(
    undefined,
  );

  const toggleEditDetailsPanel = () => {
    setIsEditActionDetailsPanelOpen(!isEditActionDetailsPanelOpen);
  };

  const toggleEditTaskPanel = (task?: IEntityTerminationTask | undefined) => {
    setSelectedTask(task);
    setIsEditTaskPanelOpen(!isEditTaskPanelOpen);
    if (isEditTaskPanelOpen) {
      setSelectedTask(undefined); // Clear selection when closing
    }
  };

  const newTaskButton = (action: string): JSX.Element => {
    return (
      <CanAccess requestedAction={action}>
        <>
          <Button appearance="primary" onClick={() => toggleEditTaskPanel()}>
            Add New Task
          </Button>
        </>
      </CanAccess>
    );
  };

  const renderHeader = (): JSX.Element => (
    <Stack horizontal>
      <h1>
        <Button
          appearance="subtle"
          icon={<ArrowLeftRegular color={iconButtonStyles.color} />}
          style={{ marginRight: 10 }}
          title="Back to the Legal Entity Terminations"
          aria-label="Back to the Legal Entity Terminations"
          onClick={(): void => {
            history.push(`/monitoring-queue/${actionItem?.companyCode}/${callerUrl}/`);
          }}
        />
        {actionItem?.companyCode} - {actionItem?.entityLegalName}
      </h1>
    </Stack>
  );

  const renderEditButton = (label: string): JSX.Element => {
    return (
      <Button
        appearance="subtle"
        title={`Edit ${label}`}
        aria-label={label}
        icon={<EditRegular color={iconButtonStyles.color} />}
        onClick={toggleEditDetailsPanel}
      />
    );
  };

  return (
    <>
      <FullWidthHeader title={renderHeader} />
      <div className={`${bodyContentContainer} lightBackground`}>
        {error && (
          <LoadingErrorMessage
            loading={loading}
            error={error}
            actionName="loading the action item data"
          />
        )}

        <Stack>
          <Stack className={classes.sectionSpacing}>
            <Card
              title={`${actionItemTerm} Details`}
              headingLevel={2}
              actionButton={
                <CanAccess requestedAction={editAction}>
                  {renderEditButton(`${actionItemTerm} Details`)}
                </CanAccess>
              }
            >
              <Stack horizontal wrap horizontalAlign="space-between" tokens={{ childrenGap: 20 }}>
                <StackColumn>
                  <SpanWithLabel
                    labelText={`${actionItemTerm} Name`}
                    value={actionItem?.description}
                  />
                  <SpanWithLabel
                    labelText={`${actionItemTerm} Owner`}
                    value={actionItem?.owner?.text}
                  />
                  <SpanWithLabel labelText="Status" value={actionItem?.status} />
                </StackColumn>
                <StackColumn>
                  <SpanWithLabel
                    labelText="Date Started"
                    value={
                      actionItem?.dateStarted
                        ? momentUtc(actionItem.dateStarted, StatDateFormats.TwoDigitDate, true)
                        : undefined
                    }
                  />
                  <SpanWithLabel
                    labelText="Date Completed"
                    value={
                      actionItem?.dateCompleted
                        ? momentUtc(actionItem.dateCompleted, StatDateFormats.TwoDigitDate, true)
                        : undefined
                    }
                  />
                </StackColumn>
              </Stack>
            </Card>
          </Stack>
          <Card
            title={`${actionItemTerm} Tasks`}
            headingLevel={2}
            isLoading={loading}
            actionButton={createTaskMutation ? newTaskButton(editAction) : undefined}
          >
            <ShimmeredDetailsListWrapper
              columns={getColumns(toggleEditTaskPanel, enableTaskDocuments)}
              items={actionItem?.tasks || []}
              singularListLabel={`${actionItemTerm} Tasks`}
              pluralListLabel={`${actionItemTerm} Tasks`}
            />
          </Card>
        </Stack>
        {isEditActionDetailsPanelOpen && (
          <EntityTerminationActionItemEditPanel
            closePanel={toggleEditDetailsPanel}
            isNewActionItem={false}
            selectedActionItem={actionItem}
            companyCode={actionItem?.companyCode ?? companyCode}
            refetchQueries={[refetchQuery]}
            updateMutation={updateMutation}
            dtoVariableName={dtoVariableName}
            isDescriptionReadOnly={isDescriptionReadOnly}
          />
        )}
        {isEditTaskPanelOpen && updateTaskMutation && (
          <EntityTerminationTaskEditPanel
            closePanel={toggleEditTaskPanel}
            isNewTask={!selectedTask}
            selectedtask={selectedTask}
            actionItemId={actionItem?.id ?? 0}
            companyCode={actionItem?.companyCode || ''}
            refetchQueries={[refetchQuery]}
            createTaskMutation={createTaskMutation}
            updateTaskMutation={updateTaskMutation}
            taskDtoVariableName={taskDtoVariableName}
            actionItemIdFieldName={actionItemIdFieldName}
            isDescriptionReadOnly={!!selectedTask?.isTemplateTask}
            actionItemStartDate={actionItem?.dateStarted}
            enableDocuments={enableTaskDocuments}
          />
        )}
      </div>
    </>
  );
};
export default EntityTerminationActionItemPage;
